/**
 * Vulnerability Remove Tag Modal Component
 *
 * Modal for removing tags from vulnerabilities with optimistic updates.
 *
 * Features:
 * - Shows only common tags across all selected vulnerabilities
 * - Optimistic updates for instant UI feedback
 * - Partial failure handling with accurate feedback
 * - Automatic cache invalidation and refresh
 */

import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { RemoveTag } from '@/sections/RemoveTag';
import { useMutation } from '@/utils/api';
import { tagRemovedError, tagRemovedSuccess } from '@/utils/tagToastMessages';

interface Props {
  open: boolean;
  onClose: () => void;
  riskKeys: string[];
  onSuccess?: () => void;
}

export const VulnerabilityRemoveTag = ({
  open,
  onClose,
  riskKeys,
  onSuccess,
}: Props) => {
  const axios = useAxios();

  const { mutate: removeTag, status: removingTag } = useMutation({
    defaultErrorMessage: 'Failed to remove tag',
    mutationFn: async (tagToRemove: string) => {
      const promises = riskKeys.map(async riskKey => {
        try {
          const currentRisk = await axios.get(`/my`, {
            params: {
              label: 'risk',
              key: riskKey,
              exact: true,
            },
          });

          const risk = currentRisk.data.risks?.[0];
          if (!risk) {
            throw new Error(`Risk not found: ${riskKey}`);
          }

          const existingTags = risk.tags || [];
          const updatedTags = existingTags.filter(
            (tag: string) => tag !== tagToRemove
          );

          const payload = {
            key: riskKey,
            name: risk.name,
            status: risk.status,
            comment: risk.comment || '',
            tags: updatedTags,
          };

          await axios.put('/risk', payload);
          return { key: riskKey, success: true };
        } catch (error) {
          console.error(
            `[VulnerabilityRemoveTag] Failed for ${riskKey}:`,
            error
          );
          throw error;
        }
      });

      const results = await Promise.allSettled(promises);

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      // Log failures for debugging
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            key: riskKeys[i],
            error: r.reason?.message || 'Unknown error',
          }));
        console.error(
          '[VulnerabilityRemoveTag] Failed operations:',
          failedResults
        );
      }

      return { succeeded, failed };
    },
    onMutate: async tagToRemove => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
      });

      // Snapshot previous value
      const previousData = queryClient.getQueriesData({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
      });

      // Optimistically remove tag
      queryClient.setQueriesData(
        {
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'risk';
          },
        },
        (old: any) => {
          if (!old?.risks) return old;

          return {
            ...old,
            risks: old.risks.map((risk: any) => {
              if (riskKeys.includes(risk.key)) {
                return {
                  ...risk,
                  tags: (risk.tags || []).filter(
                    (tag: string) => tag !== tagToRemove
                  ),
                };
              }
              return risk;
            }),
          };
        }
      );

      return { previousData };
    },
    onError: (
      _err: Error,
      _tagToRemove: string,
      context?: { previousData: Array<[any, any]> }
    ) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error('Failed to remove tag. Changes have been reverted.');
    },
    onSettled: async () => {
      // Invalidate queries
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
        refetchType: 'all',
      });
    },
  });

  const handleSubmit = async (selectedTag: string) => {
    try {
      const result = await new Promise<{ succeeded: number; failed: number }>(
        (resolve, reject) => {
          removeTag(selectedTag, {
            onSuccess: resolve,
            onError: reject,
          });
        }
      );

      // Show appropriate feedback
      if (result.succeeded > 0 && result.failed === 0) {
        const count = result.succeeded > 1 ? result.succeeded : undefined;
        toast.success(tagRemovedSuccess('vulnerability', count));
        onSuccess?.();
      } else if (result.succeeded > 0 && result.failed > 0) {
        toast.warning(
          `Tag removed from ${result.succeeded} vulnerability${result.succeeded > 1 ? 's' : ''}, ` +
            `but failed for ${result.failed}`
        );
        onSuccess?.();
      } else {
        toast.error(tagRemovedError('vulnerability'));
      }
    } catch (error) {
      console.error('[VulnerabilityRemoveTag] Mutation failed:', error);
      // Error toast already shown in onError
    }
  };

  return (
    <RemoveTag
      open={open}
      onClose={onClose}
      disabled={removingTag === 'pending'}
      onSubmit={handleSubmit}
      assetKeys={riskKeys}
      label="vulnerability"
    />
  );
};
